import React, { useEffect, useState } from "react";
import { Plus, Pencil } from "@/components/icons";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { getErrorMessage } from "@/lib/errors";
import { ErrorState } from "@/components/ListState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

// Round 26 §2.1: managed cause register. Owner/TD add, rename, deactivate.
// Every seeded cause is renameable — the words the TD uses are theirs.
export const SettingsOorzaken = () => {
  const { canWrite } = useAuth();
  const writable = canWrite();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true); setLoadError("");
    try {
      const { data } = await api.get("/oorzaken?include_inactive=true");
      setItems(data);
    } catch (err) {
      setLoadError(getErrorMessage(err, "Oorzaken konden niet geladen worden."));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm({ name: "" }); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ name: c.name }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Geef een naam."); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/oorzaken/${editing.id}`, { name: form.name });
      } else {
        await api.post("/oorzaken", { name: form.name });
      }
      toast.success(editing ? "Oorzaak bijgewerkt" : "Oorzaak toegevoegd");
      setOpen(false);
      await load();
    } catch (e) { toast.error(getErrorMessage(e, "Opslaan mislukt.")); }
    finally { setSaving(false); }
  };

  const toggleActive = async (o, active) => {
    try {
      await api.put(`/oorzaken/${o.id}`, { active });
      await load();
    } catch (e) { toast.error(getErrorMessage(e, "Wijzigen mislukt.")); }
  };

  if (loadError) return <ErrorState testid="oorzaken-load-error" message={loadError} onRetry={load} />;

  return (
    <div className="space-y-4" data-testid="settings-oorzaken">
      <Card className="p-4">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <h2 className="text-body font-semibold text-foreground">Oorzaken</h2>
            <p className="text-data text-muted-foreground">De code voor waarom iets kapot ging — hernoembaar, deactiveerbaar. De woorden die de TD gebruikt zijn van de TD.</p>
          </div>
          {writable && (
            <Button size="sm" data-testid="oorzaak-toevoegen-button" onClick={openCreate}>
              <Plus size={16} className="mr-1.5" /> Oorzaak toevoegen
            </Button>
          )}
        </div>
        {loading ? (
          <p className="text-body text-muted-foreground">Laden…</p>
        ) : items.length === 0 ? (
          <p className="text-body text-muted-foreground">Nog geen oorzaken.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Naam</TableHead>
                <TableHead className="w-[110px]">Actief</TableHead>
                <TableHead className="w-[80px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((o) => (
                <TableRow key={o.id} data-testid={`oorzaak-row-${o.id}`}>
                  <TableCell className="text-body">{o.name}</TableCell>
                  <TableCell>
                    <Switch
                      data-testid={`oorzaak-active-${o.id}`}
                      checked={Boolean(o.active)}
                      onCheckedChange={(v) => toggleActive(o, v)}
                      disabled={!writable}
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    {writable && (
                      <Button size="sm" variant="ghost" data-testid={`oorzaak-edit-${o.id}`} onClick={() => openEdit(o)}>
                        <Pencil size={16} />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent srDescription={editing ? "Hernoem deze oorzaak." : "Voeg een nieuwe oorzaak toe."}>
          <DialogHeader><DialogTitle>{editing ? "Oorzaak hernoemen" : "Oorzaak toevoegen"}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label>Naam</Label>
            <Input data-testid="oorzaak-name-input" value={form.name} onChange={(e) => setForm({ name: e.target.value })} />
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setOpen(false)}>Annuleren</Button>
            <Button data-testid="oorzaak-save-button" onClick={save} disabled={saving}>{saving ? "Bezig…" : "Opslaan"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
